import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { almacen } from '../api';
import { descargarCsv } from '../csv';
import {
  Boton,
  Cabecera,
  Cargando,
  ConAyuda,
  Fallo,
  Pastilla,
  Rotulo,
  Tarjeta,
  Vacio,
} from '../componentes';
import { cantidad, diaDeLima, soles } from '../formato';
import { tieneRecetas } from '../negocio';
import { mayuscula, useNegocio, useVocabulario } from '../vocabulario';

/**
 * Lo que se fue y ninguna receta ni ninguna merma explica.
 *
 * **Es la pantalla que convierte el libro en un control.** Todo lo demás del
 * almacén registra: entradas, salidas, quién y cuándo. Registrar no es
 * controlar. La pregunta de un dueño no es «¿cuánto hay?» —eso lo dice el
 * stock— sino «¿me están robando?», y hasta ahora el sistema tenía todos los
 * datos para responderla sin responderla.
 *
 * La cuenta es una resta que sale entera del kardex: lo que salió con motivo
 * escrito, contra lo que los conteos tuvieron que corregir. Lo segundo no
 * tiene motivo, y ese es exactamente el hueco.
 */
export function Fuga() {
  const vocabulario = useVocabulario();
  // Sin recetas, lo que explica una salida es la venta del producto tal cual:
  // hablarle de recetas a una botica es hablarle de algo que no tiene.
  const conRecetas = tieneRecetas(useNegocio());
  const explicaLaSalida = conRecetas ? 'receta' : 'venta';
  const [desde, setDesde] = useState(() => primerDiaDelMes());
  const [hasta, setHasta] = useState(() => hoy());

  const consulta = useQuery({
    queryKey: ['fuga', desde, hasta],
    queryFn: () => almacen.shrinkage(desde, hasta),
  });

  const datos = consulta.data;
  const conFuga = (datos?.lines ?? []).filter(
    (line) => Number.parseFloat(line.missing) > 0 || Number.parseFloat(line.surplus) > 0,
  );

  return (
    <>
      <Cabecera
        titulo="Lo que no cuadra"
        subtitulo={`Lo que se fue sin que ninguna ${explicaLaSalida} ni ninguna merma lo explique`} ayuda={`Compara lo que debió salir (${conRecetas ? 'ventas, producciones, mermas' : 'ventas y mermas'}) con lo que los conteos encontraron. Lo que falta sin explicación es lo que hay que investigar.`}
        acciones={
          <>
            <div className="campo campo--en-linea">
              <Rotulo htmlFor="desde" ayuda="El primer día del período que se revisa.">Del</Rotulo>
              <input
                id="desde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </div>
            <div className="campo campo--en-linea">
              <Rotulo htmlFor="hasta" ayuda="El último día del período que se revisa, incluido.">al</Rotulo>
              <input
                id="hasta"
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
              />
            </div>
            {/*
             * Es el informe que se lleva a la reunión con el encargado: tiene
             * que poder salir de la pantalla.
             */}
            <Boton
              onClick={() =>
                descargarCsv(
                  `lo-que-no-cuadra-${desde}-a-${hasta}.csv`,
                  [mayuscula(vocabulario.insumo), 'Unidad', 'Consumo explicado', 'Faltó', 'Sobró', '% sobre el consumo', 'Cuesta (S/)'],
                  conFuga.map((line) => [
                    line.supply,
                    line.unit,
                    line.explained,
                    line.missing,
                    line.surplus,
                    line.missingPercentage === null ? null : line.missingPercentage.toFixed(1),
                    line.missingValue,
                  ]),
                )
              }
              disabled={conFuga.length === 0}
            >
              Exportar
            </Boton>
          </>
        }
      />

      <div className="cuerpo">
        {consulta.isError ? (
          <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
        ) : null}

        {consulta.isLoading ? (
          <Cargando que="el descuadre" />
        ) : conFuga.length === 0 ? (
          <Vacio
            titulo="Todo lo que salió tiene su motivo"
            mensaje={`Ningún conteo de este período tuvo que corregir nada que ${conRecetas ? 'las recetas' : 'las ventas'} y las mermas no explicaran ya. Es lo que se busca; si además no se contó nada, el número no dice mucho todavía.`}
          />
        ) : (
          <>
            <div className="cifras">
              <Tarjeta>
                <p className="versal">Se fue sin explicación</p>
                <p className="cifra__valor negativo">
                  {soles(datos?.missingValue ?? '0')}
                </p>
                <p className="cifra__apoyo">
                  {conRecetas
                    ? 'Robo, porciones más grandes que la receta, o gramaje mal cargado.'
                    : 'Robo, mercadería que salió sin venderse, o una venta mal cargada.'}{' '}
                  Se arreglan distinto, pero primero hay que verlo.
                </p>
              </Tarjeta>

              <Tarjeta>
                <p className="versal">Apareció sin explicación</p>
                <p className="cifra__valor">
                  {soles(datos?.surplusValue ?? '0')}
                </p>
                <p className="cifra__apoyo">
                  {/*
                   * No es una buena noticia y se dice: una entrada sin registrar
                   * deja el costo promedio mal desde ese día, y el food cost de
                   * todo lo que lleva ese insumo viene mintiendo.
                   */}
                  Tampoco es buena noticia: es una entrada que nadie registró, y
                  el costo de ese {vocabulario.insumo} viene mal desde entonces.
                </p>
              </Tarjeta>
            </div>

            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th>{mayuscula(vocabulario.insumo)}</th>
                    <th className="numero"><ConAyuda ayuda={conRecetas ? 'Lo que salió con motivo conocido: ventas según receta, producciones, mermas registradas.' : 'Lo que salió con motivo conocido: ventas y mermas registradas.'}>Consumo explicado</ConAyuda></th>
                    <th className="numero"><ConAyuda ayuda="Lo que los conteos encontraron de menos y nada explica.">Faltó</ConAyuda></th>
                    <th className="numero"><ConAyuda ayuda="Lo que los conteos encontraron de más. Suele ser una entrada que no se registró.">Sobró</ConAyuda></th>
                    <th className="numero"><ConAyuda ayuda="Lo que faltó comparado con lo que se consumió. Un 2 % es normal; un 20 % pide revisar.">Sobre el consumo</ConAyuda></th>
                    <th className="numero"><ConAyuda ayuda="Lo que vale lo que faltó, al costo promedio.">Cuesta</ConAyuda></th>
                  </tr>
                </thead>
                <tbody>
                  {conFuga.map((line) => (
                    <tr key={line.supplyId}>
                      <td>
                        <strong>{line.supply}</strong>
                      </td>
                      <td className="numero mono apagado">
                        {cantidad(line.explained)} {line.unit}
                      </td>
                      <td className="numero mono negativo">
                        {Number.parseFloat(line.missing) === 0
                          ? '—'
                          : `${cantidad(line.missing)} ${line.unit}`}
                      </td>
                      <td className="numero mono apagado">
                        {Number.parseFloat(line.surplus) === 0
                          ? '—'
                          : `${cantidad(line.surplus)} ${line.unit}`}
                      </td>
                      <td className="numero">
                        <Proporcion valor={line.missingPercentage} />
                      </td>
                      <td className="numero">
                        <strong>{soles(line.missingValue)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/**
 * El porcentaje, cuando significa algo.
 *
 * **Nulo no se pinta como cero.** Faltar cien latas de algo que no se vendió
 * ninguna vez no es «0 % de fuga»: es que el consumo no explica nada de eso, y
 * lo que manda ahí es la plata. Pintarlo como cero escondería justo la línea
 * que hay que mirar.
 *
 * Y por encima del cien por cien se dice en veces: «se fue 200 veces lo que se
 * consumió» se entiende; «20000 %» hay que traducirlo mentalmente.
 */
function Proporcion({ valor }: { valor: number | null }) {
  if (valor === null) {
    return <Pastilla texto="Sin relación" tono="peligro" />;
  }

  if (valor === 0) return <span className="apagado">—</span>;

  if (valor > 100) {
    return (
      <Pastilla
        texto={`${Math.round(valor / 100)}× lo consumido`}
        tono="peligro"
      />
    );
  }

  return (
    <span className="mono">
      {valor.toString().replace('.', ',')} %
    </span>
  );
}

/** El mes corriente de Lima, que es el período por el que se pregunta. */
function primerDiaDelMes(): string {
  return `${diaDeLima().slice(0, 7)}-01`;
}

function hoy(): string {
  return diaDeLima();
}
